import {
  HTMLAttributes,
  useState,
  FocusEvent,
  useEffect,
  Component,
  LegacyRef,
  useRef,
  InputHTMLAttributes,
  forwardRef,
} from 'react';
import cn from 'classnames';
import LibInput from 'react-phone-number-input';
import type { Props, State, DefaultInputComponentProps } from 'react-phone-number-input';
import { useLogger } from '@packages/shared/core/common/hooks';
import { isSupportedPhoneNumberCountry } from '@packages/shared/core/common/utils/phoneNumber';
import { LocationCountry } from '@packages/shared/core/location/entities';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import PhoneFieldSelect, { PhoneFieldSelectProps } from './PhoneFieldSelect';

import styles from './PhoneField.module.scss';

type PhoneFieldProps = {
  onChange: (phoneNumber: string) => void;
  value?: string;
  onCountryChange?(country: LocationCountry): void;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  readOnly?: boolean;
  error?: string;
  errorProps?: HTMLAttributes<HTMLSpanElement>;
  defaultCountry?: LocationCountry;
  addInternationalOption?: boolean;
  smartCaret?: boolean;
  international?: boolean;
  limitMaxLength?: boolean;
  countryCallingCodeEditable?: boolean;
  focusInputOnCountrySelection?: boolean;
  label?: string;
  labelProps?: HTMLAttributes<HTMLSpanElement>;
  countrySelectProps?: Partial<PhoneFieldSelectProps>;
  inputProps?: Partial<{ className: string }>;
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
  placeholder?: string;
} & Omit<HTMLAttributes<HTMLInputElement>, 'onChange'>;

const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>((props, forwardedRef) => {
  const {
    className = '',
    placeholder: passedPlaceholder,
    label,
    id,
    onChange,
    value,
    defaultCountry,
    error,
    errorProps = {},
    labelProps = {},
    onCountryChange: passedOnCountryChange,
    inputProps: passedInputProps = {},
    countrySelectProps: passedCountrySelectProps = {},
    wrapperProps = {},
    onFocus: passedOnFocus,
    onBlur: passedOnBlur,
    focusInputOnCountrySelection = true,
    ...otherProps
  } = props;

  const logger = useLogger();

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>();

  const placeHolder = passedPlaceholder || '+____   _____   ___  ___';
  const isError = !!error;

  const handleChange = (
    phoneNumber?: Parameters<NonNullable<Props<DefaultInputComponentProps>['onChange']>>[0],
  ) => {
    onChange(phoneNumber ?? '');
  };

  const onFocus = (e: FocusEvent<HTMLElement>) => {
    setIsFocused(true);

    if (passedOnFocus) {
      passedOnFocus(e);
    }
  };

  const onBlur = (e: FocusEvent<HTMLElement>) => {
    setIsFocused(false);

    if (passedOnBlur) {
      passedOnBlur(e);
    }
  };

  const onCountrySelectFocus = () => {
    setIsFocused(true);
  };

  const onCountrySelectBlur = () => {
    setIsFocused(false);
  };

  const onSetRef = (input: HTMLInputElement) => {
    inputRef.current = input;

    if (!forwardedRef) return;

    if (typeof forwardedRef === 'function') {
      forwardedRef(input);
    } else {
      forwardedRef.current = input;
    }
  };

  const onCountryChange = (country: LocationCountry) => {
    if (passedOnCountryChange) {
      passedOnCountryChange(country);
    }

    if (!focusInputOnCountrySelection) return;

    requestAnimationFrame(forceFocus);
  };

  const forceFocus = () => {
    if (!inputRef || !inputRef.current) return;

    inputRef.current.focus();
  };

  useEffect(() => {
    if (!defaultCountry) return;

    if (!isSupportedPhoneNumberCountry(defaultCountry)) {
      logger.warn('Default country not supported for phone field.', {
        data: defaultCountry,
      });
    }
  }, [defaultCountry, logger]);

  const countrySelectProps: Partial<PhoneFieldSelectProps> = {
    ...passedCountrySelectProps,
    className: cn(styles.countrySelect, passedCountrySelectProps.className),
    onFocus: onCountrySelectFocus,
    onBlur: onCountrySelectBlur,
  };
  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    ...passedInputProps,
    className: cn(styles.input, passedInputProps.className),
  };
  const containerClasses = cn({
    [styles.container]: true,
    [styles.containerFocus]: isFocused,
    [styles.containerError]: isError,
    [className]: !!className,
  });

  return (
    <div {...wrapperProps} className={cn(styles.wrapper, wrapperProps.className)}>
      {label && (
        <label {...labelProps} htmlFor={id} className={cn(styles.label, labelProps.className)}>
          {label}
        </label>
      )}

      <LibInput
        ref={
          onSetRef as unknown as
            | LegacyRef<Component<Props<DefaultInputComponentProps>, State<Props<DefaultInputComponentProps>>>>
            | undefined
        }
        id={id}
        onChange={handleChange}
        onCountryChange={onCountryChange}
        placeholder={placeHolder}
        international
        countrySelectComponent={PhoneFieldSelect}
        countrySelectProps={countrySelectProps}
        numberInputProps={inputProps}
        value={value}
        defaultCountry={defaultCountry}
        className={containerClasses}
        focusInputOnCountrySelection={false}
        onFocus={onFocus}
        onBlur={onBlur}
        {...otherProps}
        data-testid={DataTestId.PHONE_FIELD}
      />

      <span {...errorProps} className={cn(styles.error, errorProps.className)}>
        {error}
      </span>
    </div>
  );
});

export default PhoneField;
